import React, { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { saveBudget, getBudgets } from '@/services/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  FileDown,
  Eye,
  Settings,
  Plus,
  Trash2,
  Upload,
  Image as ImageIcon,
  X,
  GripVertical,
  Calendar,
  Building2,
  User,
  FileText,
  DollarSign,
  Clock,
  CheckCircle2,
  ChevronRight,
  Save,
  History
} from 'lucide-react';
import { toast } from 'sonner';
import BudgetPreview from './BudgetPreview';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Default budget data
const defaultBudgetData = {
  // Company Info
  companyLogo: '/images/logo.jpg',
  mascotImage: '/images/hugo.png',

  // Client Info
  clientLogo: '',
  clientName: 'TodoPorcelanatos',
  projectName: 'Software de Gestión Automatizada (Compras, Contabilidad y Logística)',
  projectDate: new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }),

  // Document Title
  documentTitle: 'Presupuesto Software Gestión Automatizada',

  // Introduction
  introduction: `En base a nuestro análisis detallado de sus necesidades, presentamos la propuesta para el desarrollo de un sistema a medida orientado a resolver el flujo crítico de facturación y a dotar de inteligencia la gestión de stock y logística. Nuestro objetivo principal es eliminar la carga manual, minimizar errores mediante inteligencia artificial y enriquecer automáticamente su base de datos, optimizando así sus procesos operativos y aumentando la eficiencia general.`,
  introductionSecondary: `Esta propuesta se ha estructurado para ofrecer una visión clara de los módulos de desarrollo, sus alcances, tiempos estimados, inversión requerida y plan de pagos. Quedamos a disposición para resolver cualquier inquietud y coordinar los próximos pasos.`,

  // Modules
  modules: [
    {
      id: 'A',
      title: 'Automatización Compras/Facturación',
      duration: '45 Días',
      description: 'Solución integral para el flujo crítico actual que asegure solidez y consistencia en datos.',
      features: [
        'Tecnología OCR (AWS) para extracción precisa de datos de facturas y remitos.',
        'Validación inteligente con IA (Large Language Models) para detectar inconsistencias.',
        'Enriquecimiento automático de la base de datos con vinculación y creación de SKUS.',
        'Pre-check humano como validación final antes del registro definitivo.'
      ]
    },
    {
      id: 'B',
      title: 'Automatización Contable',
      duration: '20 Días',
      description: 'Sistema para importar y consolidar movimientos bancarios, con posibles cruces automático para identificar facturas sin asiento contable y unificar movimientos relacionados.',
      features: []
    },
    {
      id: 'C',
      title: 'Chatbot y Ventas',
      duration: '15 Días',
      description: 'Desarrollo de chatbot inteligente que permita, generar presupuestos de manera inmediata, con integración en tiempo real a la base de datos de stock y para respuestas actualizadas.',
      features: []
    },
    {
      id: 'D',
      title: 'Logística y Seguimiento',
      duration: '15 Días',
      description: 'Visibilidad completa de pedidos en tránsito, con integración GPS para notificaciones automáticas al cliente final sobre estado y ubicación.',
      features: []
    }
  ],

  // Timeline Quote
  modulesQuote: 'La prioridad se establece en el Módulo A, base fundamental para el correcto funcionamiento del sistema completo.',

  // Timeline
  timeline: {
    phases: [
      {
        name: 'Fase 1',
        description: 'Desarrollo del Módulo A (Compras y Validación IA)',
        duration: '45 días'
      },
      {
        name: 'Fase 2',
        description: 'Desarrollo paralelo de Módulos:',
        duration: '15/20 días según módulo',
        subItems: ['B (Contabilidad)', 'C (Chatbot)', 'D (Logística)']
      }
    ],
    quote: 'Este enfoque escalonado asegura que la pieza clave de gestión de compras esté operativa antes de integrar las funcionalidades complementarias.'
  },

  // Investment
  investment: {
    items: [
      { label: 'Costo total del desarrollo del software', amount: '4,000' },
      { label: 'Anticipo (pago inicial)', amount: '1,000' },
      { label: 'Saldo restante (6 cuotas mensuales)', amount: '500' },
      { label: 'Servicio de mantenimiento y soporte (mensual)', amount: '100' }
    ],
    currency: '$',
    quote: 'El mantenimiento incluye soporte técnico, monitoreo de las automatizaciones (OCR/AWS) y ajustes menores para asegurar la estabilidad.'
  },

  // Next Steps
  nextSteps: {
    content: 'Para comenzar con la Fase 1 (Módulo de Compras), aguardamos su confirmación para coordinar el pago del anticipo y programar la reunión técnica de lanzamiento (kick-off).',
    closing: 'Estamos comprometidos a acompañarlos durante todo el proceso, garantizando calidad, eficiencia y cumplimiento de los tiempos acordados.'
  },

  // Design Images (optional)
  designImages: []
};

export const BudgetEditor = () => {
  const [budgetData, setBudgetData] = useState(defaultBudgetData);
  const [activeTab, setActiveTab] = useState('general');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedBudgets, setSavedBudgets] = useState([]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const previewRef = useRef(null);

  const loadHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const data = await getBudgets();
      setSavedBudgets(data);
    } catch (error) {
      toast.error("Error al cargar el historial");
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleLoadBudget = (budget) => {
    setBudgetData(budget);
    setIsHistoryOpen(false);
    toast.success("Presupuesto cargado correctamente");
  };

  const handleSave = async () => {
    setIsSaving(true);
    toast.info('Guardando presupuesto...');
    try {
      const result = await saveBudget(budgetData);
      if (result.success) {
        toast.success('Presupuesto guardado correctamente');
      } else {
        toast.error('Error al guardar');
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  // Update a field in budgetData
  const updateField = useCallback((path, value) => {
    setBudgetData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (keys[i].includes('[')) {
          const [key, index] = keys[i].replace(']', '').split('[');
          current = current[key][parseInt(index)];
        } else {
          current = current[keys[i]];
        }
      }

      const lastKey = keys[keys.length - 1];
      if (lastKey.includes('[')) {
        const [key, index] = lastKey.replace(']', '').split('[');
        current[key][parseInt(index)] = value;
      } else {
        current[lastKey] = value;
      }

      return newData;
    });
  }, []);

  // Image upload handler
  const handleImageUpload = useCallback((field, e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateField(field, reader.result);
        toast.success('Imagen cargada correctamente');
      };
      reader.readAsDataURL(file);
    }
  }, [updateField]);

  // Add module
  const addModule = useCallback(() => {
    setBudgetData(prev => ({
      ...prev,
      modules: [
        ...prev.modules,
        {
          id: String.fromCharCode(65 + prev.modules.length),
          title: 'Nuevo Módulo',
          duration: '10 Días',
          description: 'Descripción del módulo...',
          features: []
        }
      ]
    }));
    toast.success('Módulo agregado');
  }, []);

  // Remove module
  const removeModule = useCallback((index) => {
    setBudgetData(prev => ({
      ...prev,
      modules: prev.modules.filter((_, i) => i !== index)
    }));
    toast.success('Módulo eliminado');
  }, []);

  // Add feature to module
  const addFeature = useCallback((moduleIndex) => {
    setBudgetData(prev => {
      const newModules = [...prev.modules];
      newModules[moduleIndex].features.push('Nueva característica...');
      return { ...prev, modules: newModules };
    });
  }, []);

  // Remove feature from module
  const removeFeature = useCallback((moduleIndex, featureIndex) => {
    setBudgetData(prev => {
      const newModules = [...prev.modules];
      newModules[moduleIndex].features = newModules[moduleIndex].features.filter((_, i) => i !== featureIndex);
      return { ...prev, modules: newModules };
    });
  }, []);

  // Add investment item
  const addInvestmentItem = useCallback(() => {
    setBudgetData(prev => ({
      ...prev,
      investment: {
        ...prev.investment,
        items: [...prev.investment.items, { label: 'Nuevo concepto', amount: '0' }]
      }
    }));
  }, []);

  // Remove investment item
  const removeInvestmentItem = useCallback((index) => {
    setBudgetData(prev => ({
      ...prev,
      investment: {
        ...prev.investment,
        items: prev.investment.items.filter((_, i) => i !== index)
      }
    }));
  }, []);

  // Add design image
  const handleDesignImageUpload = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBudgetData(prev => ({
          ...prev,
          designImages: [...prev.designImages, reader.result]
        }));
        toast.success('Imagen de diseño agregada');
      };
      reader.readAsDataURL(file);
    });
  }, []);

  // Remove design image
  const removeDesignImage = useCallback((index) => {
    setBudgetData(prev => ({
      ...prev,
      designImages: prev.designImages.filter((_, i) => i !== index)
    }));
    toast.success('Imagen eliminada');
  }, []);

  // Generate PDF
  const generatePDF = async () => {
    if (!previewRef.current) return;

    setIsGeneratingPDF(true);
    toast.info('Generando PDF...');

    try {
      const pages = previewRef.current.querySelectorAll('.pdf-page');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pdfWidth = 210;
      const pdfHeight = 297;

      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];

        const canvas = await html2canvas(page, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.95);

        if (i > 0) {
          pdf.addPage();
        }

        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      }

      const fileName = `Presupuesto_${budgetData.clientName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      toast.success('PDF generado correctamente');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error al generar el PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border shadow-sm">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img
                src={budgetData.companyLogo}
                alt="TuriaDesign"
                className="h-10 w-auto"
              />
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold text-foreground">
                  <span className="logo-turia">Turia</span>
                  <span className="logo-design">Design</span>
                  <span className="text-muted-foreground ml-1 text-sm font-normal">solutions</span>
                </h1>
                <p className="text-xs text-muted-foreground">Generador de Presupuestos</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActiveTab('preview')}
                className="hidden sm:flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Vista previa
              </Button>

              <Dialog open={isHistoryOpen} onOpenChange={(open) => {
                setIsHistoryOpen(open);
                if (open) loadHistory();
              }}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="hidden sm:flex items-center gap-2"
                  >
                    <History className="w-4 h-4" />
                    Historial
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[700px] max-h-[80vh]">
                  <DialogHeader>
                    <DialogTitle>Presupuestos Guardados</DialogTitle>
                  </DialogHeader>
                  <ScrollArea className="mt-4 max-h-[60vh]">
                    <div className="space-y-4 pr-4">
                      {isLoadingHistory ? (
                        <p className="text-center py-8 text-muted-foreground">Cargando...</p>
                      ) : savedBudgets.length === 0 ? (
                        <p className="text-center py-8 text-muted-foreground">No hay presupuestos guardados aún.</p>
                      ) : (
                        savedBudgets.map((budget, idx) => (
                          <div key={budget.id || idx} className="flex items-center justify-between p-4 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors">
                            <div className="space-y-1">
                              <h4 className="font-medium">{budget.clientName || 'Sin Cliente'}</h4>
                              <p className="text-sm text-muted-foreground line-clamp-1">{budget.projectName || 'Sin Proyecto'}</p>
                              <p className="text-xs text-muted-foreground">
                                {budget.savedAt ? new Date(budget.savedAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Fecha desconocida'}
                              </p>
                            </div>
                            <Button size="sm" onClick={() => handleLoadBudget(budget)}>
                              Cargar
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </DialogContent>
              </Dialog>

              <Button
                variant="outline"
                size="sm"
                onClick={handleSave}
                disabled={isSaving}
                className="hidden sm:flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Guardando...' : 'Guardar'}
              </Button>
              <Button
                onClick={generatePDF}
                disabled={isGeneratingPDF}
                className="bg-primary hover:bg-primary-dark text-primary-foreground btn-hover-lift"
              >
                <FileDown className="w-4 h-4 mr-2" />
                {isGeneratingPDF ? 'Generando...' : 'Generar PDF'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Editor Panel */}
          <div className="space-y-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid grid-cols-5 w-full bg-muted">
                <TabsTrigger value="general" className="text-xs sm:text-sm">
                  <Settings className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">General</span>
                </TabsTrigger>
                <TabsTrigger value="modules" className="text-xs sm:text-sm">
                  <FileText className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Módulos</span>
                </TabsTrigger>
                <TabsTrigger value="timeline" className="text-xs sm:text-sm">
                  <Clock className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Tiempo</span>
                </TabsTrigger>
                <TabsTrigger value="investment" className="text-xs sm:text-sm">
                  <DollarSign className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Inversión</span>
                </TabsTrigger>
                <TabsTrigger value="preview" className="text-xs sm:text-sm xl:hidden">
                  <Eye className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Preview</span>
                </TabsTrigger>
              </TabsList>

              {/* General Tab */}
              <TabsContent value="general" className="mt-6 space-y-6 animate-fadeIn">
                {/* Client Logo */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-primary" />
                      Logo del Cliente (Opcional)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      {budgetData.clientLogo ? (
                        <div className="relative">
                          <img
                            src={budgetData.clientLogo}
                            alt="Logo del cliente"
                            className="h-16 w-auto rounded border border-border"
                          />
                          <button
                            onClick={() => updateField('clientLogo', '')}
                            className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 hover:bg-destructive/90"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <label className="drop-zone flex-1 cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleImageUpload('clientLogo', e)}
                          />
                          <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">
                            Click para subir logo del cliente
                          </p>
                        </label>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Project Info */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <User className="w-4 h-4 text-primary" />
                      Información del Proyecto
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="documentTitle">Título del Documento</Label>
                      <Input
                        id="documentTitle"
                        value={budgetData.documentTitle}
                        onChange={(e) => updateField('documentTitle', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="clientName">Nombre del Cliente</Label>
                      <Input
                        id="clientName"
                        value={budgetData.clientName}
                        onChange={(e) => updateField('clientName', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="projectName">Nombre del Proyecto</Label>
                      <Input
                        id="projectName"
                        value={budgetData.projectName}
                        onChange={(e) => updateField('projectName', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="projectDate">Fecha</Label>
                      <Input
                        id="projectDate"
                        value={budgetData.projectDate}
                        onChange={(e) => updateField('projectDate', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Introduction */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="w-4 h-4 text-primary" />
                      Introducción
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="introduction">Párrafo Principal</Label>
                      <Textarea
                        id="introduction"
                        value={budgetData.introduction}
                        onChange={(e) => updateField('introduction', e.target.value)}
                        rows={5}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="introductionSecondary">Párrafo Secundario</Label>
                      <Textarea
                        id="introductionSecondary"
                        value={budgetData.introductionSecondary}
                        onChange={(e) => updateField('introductionSecondary', e.target.value)}
                        rows={3}
                        className="mt-1"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Design Images */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ImageIcon className="w-4 h-4 text-primary" />
                      Imágenes de Diseño (Opcional)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                      {budgetData.designImages.map((img, index) => (
                        <div key={index} className="relative aspect-video bg-muted rounded-lg overflow-hidden">
                          <img
                            src={img}
                            alt={`Diseño ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <button
                            onClick={() => removeDesignImage(index)}
                            className="absolute top-2 right-2 bg-destructive text-destructive-foreground rounded-full p-1 hover:bg-destructive/90"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <label className="drop-zone block cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleDesignImageUpload}
                      />
                      <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Click para agregar imágenes de diseño/entregas
                      </p>
                    </label>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Modules Tab */}
              <TabsContent value="modules" className="mt-6 space-y-4 animate-fadeIn">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Módulos de Desarrollo</h3>
                  <Button onClick={addModule} size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Módulo
                  </Button>
                </div>

                {budgetData.modules.map((module, moduleIndex) => (
                  <Card key={moduleIndex} className="relative">
                    <button
                      onClick={() => removeModule(moduleIndex)}
                      className="absolute top-3 right-3 text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                          {module.id}
                        </div>
                        <Input
                          value={module.title}
                          onChange={(e) => {
                            const newModules = [...budgetData.modules];
                            newModules[moduleIndex].title = e.target.value;
                            setBudgetData(prev => ({ ...prev, modules: newModules }));
                          }}
                          className="font-semibold text-base"
                          placeholder="Título del módulo"
                        />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <Label>ID</Label>
                          <Input
                            value={module.id}
                            onChange={(e) => {
                              const newModules = [...budgetData.modules];
                              newModules[moduleIndex].id = e.target.value;
                              setBudgetData(prev => ({ ...prev, modules: newModules }));
                            }}
                            className="mt-1"
                          />
                        </div>
                        <div className="flex-1">
                          <Label>Duración</Label>
                          <Input
                            value={module.duration}
                            onChange={(e) => {
                              const newModules = [...budgetData.modules];
                              newModules[moduleIndex].duration = e.target.value;
                              setBudgetData(prev => ({ ...prev, modules: newModules }));
                            }}
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Descripción</Label>
                        <Textarea
                          value={module.description}
                          onChange={(e) => {
                            const newModules = [...budgetData.modules];
                            newModules[moduleIndex].description = e.target.value;
                            setBudgetData(prev => ({ ...prev, modules: newModules }));
                          }}
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <Label>Características</Label>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => addFeature(moduleIndex)}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Agregar
                          </Button>
                        </div>
                        {module.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center gap-2 mb-2">
                            <GripVertical className="w-4 h-4 text-muted-foreground" />
                            <Input
                              value={feature}
                              onChange={(e) => {
                                const newModules = [...budgetData.modules];
                                newModules[moduleIndex].features[featureIndex] = e.target.value;
                                setBudgetData(prev => ({ ...prev, modules: newModules }));
                              }}
                              className="flex-1"
                            />
                            <button
                              onClick={() => removeFeature(moduleIndex, featureIndex)}
                              className="text-muted-foreground hover:text-destructive"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Nota sobre Módulos</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={budgetData.modulesQuote}
                      onChange={(e) => updateField('modulesQuote', e.target.value)}
                      rows={2}
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Timeline Tab */}
              <TabsContent value="timeline" className="mt-6 space-y-4 animate-fadeIn">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary" />
                      Cronograma Estimado
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {budgetData.timeline.phases.map((phase, index) => (
                      <div key={index} className="p-4 bg-muted rounded-lg">
                        <div className="flex gap-4 mb-2">
                          <div className="flex-1">
                            <Label>Nombre de Fase</Label>
                            <Input
                              value={phase.name}
                              onChange={(e) => {
                                const newPhases = [...budgetData.timeline.phases];
                                newPhases[index].name = e.target.value;
                                setBudgetData(prev => ({
                                  ...prev,
                                  timeline: { ...prev.timeline, phases: newPhases }
                                }));
                              }}
                              className="mt-1"
                            />
                          </div>
                          <div className="flex-1">
                            <Label>Duración</Label>
                            <Input
                              value={phase.duration}
                              onChange={(e) => {
                                const newPhases = [...budgetData.timeline.phases];
                                newPhases[index].duration = e.target.value;
                                setBudgetData(prev => ({
                                  ...prev,
                                  timeline: { ...prev.timeline, phases: newPhases }
                                }));
                              }}
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Descripción</Label>
                          <Textarea
                            value={phase.description}
                            onChange={(e) => {
                              const newPhases = [...budgetData.timeline.phases];
                              newPhases[index].description = e.target.value;
                              setBudgetData(prev => ({
                                ...prev,
                                timeline: { ...prev.timeline, phases: newPhases }
                              }));
                            }}
                            rows={2}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    ))}

                    <Separator />

                    <div>
                      <Label>Nota del Cronograma</Label>
                      <Textarea
                        value={budgetData.timeline.quote}
                        onChange={(e) => updateField('timeline.quote', e.target.value)}
                        rows={2}
                        className="mt-1"
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Investment Tab */}
              <TabsContent value="investment" className="mt-6 space-y-4 animate-fadeIn">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-primary" />
                      Inversión y Esquema de Pagos
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="mb-4">
                      <Label>Moneda</Label>
                      <Input
                        value={budgetData.investment.currency}
                        onChange={(e) => updateField('investment.currency', e.target.value)}
                        className="mt-1 w-24"
                        placeholder="$"
                      />
                    </div>

                    {budgetData.investment.items.map((item, index) => (
                      <div key={index} className="flex items-center gap-4 p-3 bg-muted rounded-lg">
                        <div className="flex-1">
                          <Input
                            value={item.label}
                            onChange={(e) => {
                              const newItems = [...budgetData.investment.items];
                              newItems[index].label = e.target.value;
                              setBudgetData(prev => ({
                                ...prev,
                                investment: { ...prev.investment, items: newItems }
                              }));
                            }}
                            placeholder="Concepto"
                          />
                        </div>
                        <div className="w-32">
                          <Input
                            value={item.amount}
                            onChange={(e) => {
                              const newItems = [...budgetData.investment.items];
                              newItems[index].amount = e.target.value;
                              setBudgetData(prev => ({
                                ...prev,
                                investment: { ...prev.investment, items: newItems }
                              }));
                            }}
                            placeholder="Monto"
                          />
                        </div>
                        <button
                          onClick={() => removeInvestmentItem(index)}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}

                    <Button onClick={addInvestmentItem} variant="outline" size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar Concepto
                    </Button>

                    <Separator />

                    <div>
                      <Label>Nota sobre Mantenimiento</Label>
                      <Textarea
                        value={budgetData.investment.quote}
                        onChange={(e) => updateField('investment.quote', e.target.value)}
                        rows={2}
                        className="mt-1"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                      Siguientes Pasos
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Contenido</Label>
                      <Textarea
                        value={budgetData.nextSteps.content}
                        onChange={(e) => updateField('nextSteps.content', e.target.value)}
                        rows={3}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Cierre</Label>
                      <Textarea
                        value={budgetData.nextSteps.closing}
                        onChange={(e) => updateField('nextSteps.closing', e.target.value)}
                        rows={2}
                        className="mt-1"
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Preview Tab (Mobile) */}
              <TabsContent value="preview" className="mt-6 xl:hidden">
                <ScrollArea className="h-[calc(100vh-200px)]">
                  <BudgetPreview data={budgetData} ref={previewRef} />
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </div>

          {/* Preview Panel (Desktop) */}
          <div className="hidden xl:block sticky top-24 h-[calc(100vh-120px)]">
            <Card className="h-full overflow-hidden">
              <CardHeader className="py-3 px-4 border-b border-border bg-muted/50">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Vista Previa del PDF
                  </CardTitle>
                  <Button
                    onClick={generatePDF}
                    disabled={isGeneratingPDF}
                    size="sm"
                    className="bg-primary hover:bg-primary-dark"
                  >
                    <FileDown className="w-4 h-4 mr-2" />
                    {isGeneratingPDF ? 'Generando...' : 'Descargar PDF'}
                  </Button>
                </div>
              </CardHeader>
              <ScrollArea className="h-[calc(100%-60px)] custom-scrollbar">
                <div className="p-4 bg-muted/30">
                  <BudgetPreview data={budgetData} ref={previewRef} />
                </div>
              </ScrollArea>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetEditor;
